import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtDateTime, fmtDuration, errMsg } from "@/lib/api";
import { StatusPill, Empty, Skeletons, SectionHead } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, FileAudio, Trash2, Radio, Eye, Users, Pencil } from "lucide-react";

export default function Meetings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [del, setDel] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/meetings", { params: { q, status: status === "all" ? "" : status } });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const remove = async () => {
    try {
      await api.delete(`/meetings/${del.id}`);
      toast.success("Rapat dihapus");
      setDel(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-7" data-testid="meetings-page">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="label-xs">Manajemen Rapat</p>
          <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Meeting</h1>
          <p className="mt-2 text-sm text-slate-500">{rows.length} rapat terdaftar pada sistem.</p>
        </div>
        <Button className="h-11 rounded-xl grad-brand text-white px-5" onClick={() => nav("/meetings/new")} data-testid="create-meeting-btn">
          <Plus size={16} className="mr-2" /> Buat Rapat
        </Button>
      </div>

      <div className="clay p-5">
        <form className="flex flex-wrap gap-3" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari judul, agenda, atau tag…"
              className="h-11 pl-10 rounded-xl" data-testid="meeting-search-input" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 w-[190px] rounded-xl" data-testid="meeting-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="scheduled">Terjadwal</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="processing">Diproses</SelectItem>
              <SelectItem value="completed">Selesai</SelectItem>
              <SelectItem value="cancelled">Dibatalkan</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" variant="outline" className="h-11 rounded-xl border-slate-200" data-testid="meeting-search-btn">Terapkan</Button>
        </form>
      </div>

      <div className="clay overflow-hidden" data-testid="meetings-table-card">
        {loading ? (
          <div className="p-6"><Skeletons rows={5} className="h-14" /></div>
        ) : rows.length === 0 ? (
          <Empty icon={FileAudio} title="Belum ada rapat" sub="Buat rapat baru untuk mulai merekam dan menganalisis notulen."
            action={<Button className="rounded-xl grad-brand text-white" onClick={() => nav("/meetings/new")}>Buat Rapat</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-slate-100">
                  <TableHead className="p-4 label-xs">Rapat</TableHead>
                  <TableHead className="p-4 label-xs">Jadwal</TableHead>
                  <TableHead className="p-4 label-xs">Divisi</TableHead>
                  <TableHead className="p-4 label-xs">Peserta</TableHead>
                  <TableHead className="p-4 label-xs">Durasi</TableHead>
                  <TableHead className="p-4 label-xs">Akurasi</TableHead>
                  <TableHead className="p-4 label-xs">Status</TableHead>
                  <TableHead className="p-4 label-xs text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((m) => (
                  <TableRow key={m.id} className="border-slate-100 hover:bg-slate-50/70 transition-colors" data-testid={`meeting-row-${m.id}`}>
                    <TableCell className="p-4">
                      <Link to={`/meetings/${m.id}`} className="font-semibold text-slate-800 hover:text-brand-700 transition-colors">{m.title}</Link>
                      <p className="mt-0.5 text-xs text-slate-500 line-clamp-1">{m.location || "—"} · {m.segment_count} segmen</p>
                    </TableCell>
                    <TableCell className="p-4 text-sm text-slate-600 whitespace-nowrap">{fmtDateTime(m.scheduled_at)}</TableCell>
                    <TableCell className="p-4 text-sm text-slate-600">{m.division || "—"}</TableCell>
                    <TableCell className="p-4"><span className="inline-flex items-center gap-1.5 text-sm text-slate-600"><Users size={14} className="text-slate-400" />{m.participant_count}</span></TableCell>
                    <TableCell className="p-4 font-mono text-sm text-slate-600">{m.duration_seconds ? fmtDuration(m.duration_seconds) : "—"}</TableCell>
                    <TableCell className="p-4 font-mono text-sm text-slate-600">{m.accuracy ? `${(m.accuracy * 100).toFixed(1)}%` : "—"}</TableCell>
                    <TableCell className="p-4"><StatusPill status={m.status} /></TableCell>
                    <TableCell className="p-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => nav(`/meetings/${m.id}`)} data-testid={`view-meeting-${m.id}`}><Eye size={15} className="text-slate-500" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => nav(`/meetings/${m.id}/edit`)} data-testid={`edit-meeting-${m.id}`}><Pencil size={15} className="text-slate-500" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => nav(`/live/${m.id}`)} data-testid={`live-meeting-${m.id}`}><Radio size={15} className="text-brand-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDel(m)} data-testid={`delete-meeting-${m.id}`}><Trash2 size={15} className="text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="delete-meeting-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus rapat ini?</AlertDialogTitle>
            <AlertDialogDescription>
              Transkrip, ringkasan AI, dan action items untuk “{del?.title}” akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
